import { useCallback, useRef } from 'react'
import axios from 'axios'
import removeMarkdown from 'remove-markdown'
import { useChatStore } from '../stores/useChatStore'
import { useUIStore } from '../stores/useUIStore'
import { useArcanoStore } from '../stores/useArcanoStore'
import { useAuthContext } from '../contexts/AuthContext'
import { ARCANA_ENABLED, PROJECTS_ENABLED } from '../constants/featureFlags'
import {
  regenerateResponse,
  getMagicPromptChat,
  updateMessageMetadata,
} from '../services/chatService'
import {
  generateInlineExplanationVisuals,
} from '../services/generationService'
import { extractErrorMessage } from '../utils/errorHandler'
import { useShallow } from 'zustand/react/shallow'
import { toast } from 'react-toastify'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

export const useChatActions = () => {
  const { token, logout } = useAuthContext()

  const {
    selectedConversation,
    messages,
    newMessage,
    setMessages,
    addMessage,
    updateMessage,
    setNewMessage,
    setIsSendingMessage,
    setCurrentThinkingStatus,
    setError,
    setLastTokenUsage,
    setCopiedMessageId,
    setRegeneratingMessageId,
    setStreamingMessageId,
    setChatElapsedSeconds,
    setMessageMetadata,
    resetChatState,
  } = useChatStore(
    useShallow(state => ({
      selectedConversation: state.selectedConversation,
      messages: state.messages,
      newMessage: state.newMessage,
      setMessages: state.setMessages,
      addMessage: state.addMessage,
      updateMessage: state.updateMessage,
      setNewMessage: state.setNewMessage,
      setIsSendingMessage: state.setIsSendingMessage,
      setCurrentThinkingStatus: state.setCurrentThinkingStatus,
      setError: state.setError,
      setLastTokenUsage: state.setLastTokenUsage,
      setCopiedMessageId: state.setCopiedMessageId,
      setRegeneratingMessageId: state.setRegeneratingMessageId,
      setStreamingMessageId: state.setStreamingMessageId,
      setChatElapsedSeconds: state.setChatElapsedSeconds,
      setMessageMetadata: state.setMessageMetadata,
      resetChatState: state.resetChatState,
    }))
  )

  const {
    selectedFiles,
    enableKnowledgeBase,
    enableDeepAnalysis,
    clearFiles,
    setIsMagicLoading,
    magicPromptLength,
  } = useUIStore(
    useShallow(state => ({
      selectedFiles: state.selectedFiles,
      enableKnowledgeBase: state.enableKnowledgeBase,
      enableDeepAnalysis: state.enableDeepAnalysis,
      clearFiles: state.clearFiles,
      setIsMagicLoading: state.setIsMagicLoading,
      magicPromptLength: state.magicPromptLength,
    }))
  )

  const { selectedArcano, selectedProject } = useArcanoStore(
    useShallow(state => ({
      selectedArcano: state.selectedArcano,
      selectedProject: state.selectedProject,
    }))
  )

  // Refs
  const requestAbortControllerRef = useRef(null)
  const typingIntervalRef = useRef(null)
  const chatStartTimeRef = useRef(null)
  const chatTimerIntervalRef = useRef(null)

  const toAbsoluteMediaUrl = maybeRelativeUrl => {
    if (!maybeRelativeUrl) return null
    if (maybeRelativeUrl.startsWith('http')) return maybeRelativeUrl
    const apiBase = (
      process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
    ).replace('/api/v1', '')
    return `${apiBase}${maybeRelativeUrl}`
  }

  const persistMetadataWithRetry = async (
    authToken,
    conversationId,
    messageId,
    payload,
    retries = 3
  ) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        await updateMessageMetadata(authToken, conversationId, messageId, payload)
        return
      } catch (err) {
        console.warn(
          `Metadata persist attempt ${attempt}/${retries} failed`,
          err
        )
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 1000 * attempt))
        }
      }
    }
    console.error('All metadata persist attempts failed for message', messageId)
  }

  const splitIntoHeadingSections = text => {
    const lines = text.split('\n')
    const sections = []
    let current = { title: '', lines: [] }

    for (const line of lines) {
      const headingMatch = line.match(/^#{1,4}\s+(.+)/)
      if (headingMatch && current.lines.length > 0) {
        sections.push(current)
        current = { title: headingMatch[1].trim(), lines: [] }
      } else if (headingMatch && current.lines.length === 0) {
        current.title = headingMatch[1].trim()
      } else {
        current.lines.push(line)
      }
    }
    if (current.lines.length > 0 || current.title) sections.push(current)
    return sections
      .map((s, idx) => ({
        idx,
        title: s.title || '',
        text: s.lines.join('\n').trim(),
      }))
      .filter(s => s.text.length > 0)
  }

  const extractVisualCandidates = content => {
    if (!content || typeof content !== 'string') return []
    const cleaned = content.replace(/```[\s\S]*?```/g, '').replace(/\r/g, '').trim()
    if (!cleaned) return []
    // Be conservative: avoid visuals on short/simple answers.
    if (cleaned.length < 520) return []

    const sections = splitIntoHeadingSections(cleaned)
    if (sections.length < 2) return []

    const scored = sections
      .map(section => {
        const normalized = section.text.toLowerCase()
        const hasStructureCue =
          /\b(step|steps|process|workflow|architecture|timeline|roadmap|comparison|processo|passaggi|flusso|architettura|confronto|schema|diagramma|struttura|rete)\b/.test(
            normalized
          ) || /\n\s*[-*]\s+/.test(section.text) || /\d+\.\s+/.test(section.text)

        const textLen = section.text.length
        const lengthScore = Math.min(textLen / 200, 2)
        const structureBonus = hasStructureCue ? 2.5 : 0
        const score = lengthScore + structureBonus

        // Keep sizing conservative and stable to avoid oversized visuals.
        const sizeHint =
          textLen > 700 && hasStructureCue
            ? 'large'
            : textLen > 280 || hasStructureCue
            ? 'medium'
            : 'small'

        return { ...section, score, sizeHint }
      })
      .filter(item => item.score >= 1.5)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .sort((a, b) => a.idx - b.idx)

    let largeVisualAlreadyAssigned = false
    return scored.map(item => {
      let normalizedSizeHint = item.sizeHint
      if (normalizedSizeHint === 'large') {
        if (largeVisualAlreadyAssigned) {
          normalizedSizeHint = 'medium'
        } else {
          largeVisualAlreadyAssigned = true
        }
      }

      return {
        sectionIndex: item.idx,
        section_title: item.title || `Sezione ${item.idx + 1}`,
        caption: item.title || `Sezione ${item.idx + 1}`,
        size_hint: normalizedSizeHint,
        visual_prompt: `Create a clean, professional educational illustration for this concept. Style: ${
          normalizedSizeHint === 'large'
            ? 'detailed diagram with labels and flow arrows'
            : normalizedSizeHint === 'medium'
            ? 'clear focused diagram with key labels'
            : 'simple icon-style pictogram, minimal'
        }.\n\nTopic: ${item.title}\n\nContent:\n${item.text.slice(0, 900)}`,
      }
    })
  }

  const generateInlineVisualExplanationForMessage = useCallback(
    async (messageId, content, options = {}) => {
      if (!token || !messageId) return
      const force = Boolean(options.force)
      const conversationIdFromOptions = options.conversationId || null

      let candidates = extractVisualCandidates(content)
      if (force && candidates.length === 0) {
        const fallbackText =
          typeof content === 'string'
            ? content.replace(/```[\s\S]*?```/g, '').replace(/\s+/g, ' ').trim()
            : ''
        if (fallbackText) {
          candidates = [
            {
              section_title: 'Panoramica',
              caption: 'Visuale riassuntiva della spiegazione.',
              visual_prompt: `Create a concise educational visual that summarizes this explanation in a clear, article-like style.\n\n${fallbackText.slice(
                0,
                1000
              )}`,
            },
          ]
        }
      }
      if (candidates.length === 0) return

      const requestItems = candidates.map(item => ({
        ...item,
        model: 'fast',
      }))

      const placeholderItems = requestItems.map(item => ({
        section_title: item.section_title,
        caption: item.caption,
        model: item.model,
        size_hint: item.size_hint || 'medium',
        sectionIndex: item.sectionIndex,
        status: 'loading',
      }))

      setMessageMetadata(messageId, {
        visual_explanation: {
          status: 'loading',
          style: 'article',
          items: placeholderItems,
          generated_at: new Date().toISOString(),
        },
      })

      const generateSingleVisual = async (item, index) => {
        try {
          const result = await generateInlineExplanationVisuals(token, [item])
          const generated = result?.items?.[0]
          if (!generated) throw new Error('No result')
          return {
            ...generated,
            image_url: toAbsoluteMediaUrl(generated.image_url),
            size_hint: item.size_hint || 'medium',
            sectionIndex: item.sectionIndex,
          }
        } catch (err) {
          console.warn(`Visual ${index} generation failed`, err)
          return {
            section_title: item.section_title,
            caption: item.caption,
            model: item.model,
            size_hint: item.size_hint || 'medium',
            sectionIndex: item.sectionIndex,
            status: 'error',
            error_message: 'Impossibile generare questa visuale.',
          }
        }
      }

      const settled = new Array(requestItems.length).fill(null)
      let lastPersistedReadyCount = 0

      const updateVisualState = () => {
        const currentItems = settled.map(
          (item, i) => item || placeholderItems[i]
        )
        const allDone = settled.every(s => s !== null)
        const hasReady = settled.some(s => s?.status === 'ready')
        const readyCount = settled.filter(s => s?.status === 'ready').length

        const payload = {
          visual_explanation: {
            status: allDone ? (hasReady ? 'ready' : 'error') : 'loading',
            style: 'article',
            items: currentItems,
            generated_at: new Date().toISOString(),
          },
        }
        setMessageMetadata(messageId, payload)

        const activeConversationId =
          conversationIdFromOptions || useChatStore.getState().selectedConversation?.id
        const shouldPersistNow =
          Boolean(activeConversationId) &&
          hasReady &&
          (allDone || readyCount > lastPersistedReadyCount)

        if (shouldPersistNow) {
          lastPersistedReadyCount = readyCount
          void persistMetadataWithRetry(
            token,
            activeConversationId,
            messageId,
            payload
          )
        }
      }

      requestItems.forEach((item, index) => {
        generateSingleVisual(item, index).then(result => {
          settled[index] = result
          updateVisualState()
        })
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMessageMetadata, token]
  )

  /**
   * Handles sending a new message
   */
  const handleSendMessage = useCallback(
    async e => {
      if (e) e.preventDefault()

      if (
        (!newMessage.trim() && selectedFiles.length === 0) ||
        !token ||
        useChatStore.getState().isSendingMessage
      )
        return

      // Cancel any ongoing requests
      if (requestAbortControllerRef.current) {
        requestAbortControllerRef.current.abort()
      }
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current)
      }

      // Create optimistic user message
      const optimisticUserMessage = {
        message_id: `temp-user-${Date.now()}`,
        role: 'user',
        content:
          newMessage +
          (selectedFiles.length > 0
            ? `\n\n*[Allegato: ${selectedFiles.map(f => f.name).join(', ')}]*`
            : ''),
        timestamp: new Date().toISOString(),
      }

      addMessage(optimisticUserMessage)

      // Save current values
      const currentInput = newMessage
      const currentFiles = [...selectedFiles]

      // Clear input
      setNewMessage('')
      clearFiles()
      setIsSendingMessage(true)
      setCurrentThinkingStatus('F.R.A.N.C.A. sta elaborando la tua richiesta...')
      setError('')
      setLastTokenUsage(null)
      setChatElapsedSeconds(0)

      // Start timer - update every second instead of 100ms
      chatStartTimeRef.current = Date.now()
      chatTimerIntervalRef.current = setInterval(() => {
        if (chatStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - chatStartTimeRef.current) / 1000)
          setChatElapsedSeconds(elapsed)
        }
      }, 1000) // Changed from 100ms to 1000ms

      // Prepare form data
      const formData = new FormData()
      formData.append('content', currentInput)
      if (selectedConversation)
        formData.append('conversation_id', selectedConversation.id)
      currentFiles.forEach(file => formData.append('files', file))
      formData.append('enable_knowledge_base', enableKnowledgeBase.toString())
      formData.append('enable_deep_analysis', enableDeepAnalysis.toString())

      if (ARCANA_ENABLED && selectedArcano && !selectedConversation) {
        formData.append('arcana_id', selectedArcano.arcana_id)
      }
      if (
        PROJECTS_ENABLED &&
        selectedProject &&
        !selectedConversation
      ) {
        const projectId = selectedProject.id || selectedProject._id
        formData.append('project_id', projectId)
      }

      const controller = new AbortController()
      requestAbortControllerRef.current = controller

      try {
        const response = await axios.post(`${API_BASE_URL}/chat/message`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })

        requestAbortControllerRef.current = null
        setCurrentThinkingStatus('')

        const {
          user_message,
          assistant_message,
          conversation_id,
          title,
          active_arcana_id,
          is_new_conversation,
          token_usage,
          file_processed_summary,
        } = response.data

        const assistantMessageWithHints = { ...assistant_message }

        // Replace optimistic message with real one
        const finalUserMessage = {
          ...user_message,
          fileSummary: file_processed_summary,
        }

        setMessages(
          messages
            .filter(msg => msg.message_id !== optimisticUserMessage.message_id)
            .concat(finalUserMessage)
        )

        // Simulate typing effect for assistant message with optimized speed
        const assistantMessageId = assistantMessageWithHints.message_id
        const assistantShell = { ...assistantMessageWithHints, content: '' }
        addMessage(assistantShell)
        setStreamingMessageId(assistantMessageId)
        // Start visual generation immediately (in parallel with typing animation)
        void generateInlineVisualExplanationForMessage(
          assistantMessageId,
          assistantMessageWithHints.content,
          { conversationId: conversation_id }
        )

        let currentContent = ''
        const words = assistantMessageWithHints.content.split(/(\s+)/)
        let wordIndex = 0

        // Use requestAnimationFrame for smoother updates
        const startTime = Date.now()
        const WORDS_PER_SECOND = 30 // Adjustable typing speed

        const animateTyping = () => {
          const elapsed = Date.now() - startTime
          const targetIndex = Math.floor((elapsed / 1000) * WORDS_PER_SECOND)

          if (targetIndex >= words.length) {
            // Animation complete
            clearInterval(typingIntervalRef.current)
            typingIntervalRef.current = null
            setStreamingMessageId(null)
            setIsSendingMessage(false)

            if (chatTimerIntervalRef.current) {
              clearInterval(chatTimerIntervalRef.current)
              chatTimerIntervalRef.current = null
            }
            chatStartTimeRef.current = null

            const currentAssistantMessage = useChatStore
              .getState()
              .messages.find(m => m.message_id === assistantMessageId)
            const liveMetadata = currentAssistantMessage?.metadata || {}
            const serverMetadata = assistantMessageWithHints.metadata || {}
            const mergedMetadata = { ...serverMetadata }
            if (liveMetadata.visual_explanation) {
              mergedMetadata.visual_explanation = liveMetadata.visual_explanation
            }
            updateMessage(assistantMessageId, {
              ...assistantMessageWithHints,
              metadata: mergedMetadata,
            })
          } else if (targetIndex > wordIndex) {
            // Add words in batches for better performance
            while (wordIndex <= targetIndex && wordIndex < words.length) {
              currentContent += words[wordIndex]
              wordIndex++
            }
            updateMessage(assistantMessageId, { content: currentContent })
          }
        }

        // Use setInterval with reasonable frequency (30fps is smooth enough)
        typingIntervalRef.current = setInterval(animateTyping, 33) // ~30fps

        setLastTokenUsage(token_usage)

        // Update conversations list
        const newOrUpdatedConvSummary = {
          id: conversation_id,
          title,
          created_at: is_new_conversation
            ? new Date(user_message.timestamp).toISOString()
            : selectedConversation?.created_at ||
              new Date(user_message.timestamp).toISOString(),
          updated_at: new Date(assistant_message.timestamp).toISOString(),
          active_arcana_id,
          project_id:
            PROJECTS_ENABLED
              ? selectedProject?.id || selectedProject?._id || null
              : null,
        }

        if (is_new_conversation) {
          useChatStore.getState().addConversation(newOrUpdatedConvSummary)
          useChatStore.getState().setSelectedConversation(newOrUpdatedConvSummary)
        } else {
          useChatStore
            .getState()
            .updateConversation(conversation_id, newOrUpdatedConvSummary)
        }
      } catch (err) {
        requestAbortControllerRef.current = null

        if (!axios.isCancel(err)) {
          console.error('Error sending message:', err)
          const errorMsg =
            err.response?.data?.detail || 'Impossibile inviare il messaggio. Riprova.'
		  toast.error(errorMsg)

          // Remove optimistic message on error
          setMessages(
            messages.filter(msg => msg.message_id !== optimisticUserMessage.message_id)
          )

          if (err.response?.status === 401 || err.response?.status === 403) {
            logout()
          }

          setIsSendingMessage(false)
          setCurrentThinkingStatus('')

          if (chatTimerIntervalRef.current) {
            clearInterval(chatTimerIntervalRef.current)
            chatTimerIntervalRef.current = null
          }
          chatStartTimeRef.current = null
        }
      }
    },
    [
      newMessage,
      selectedFiles,
      token,
      selectedConversation,
      enableKnowledgeBase,
      enableDeepAnalysis,
      selectedArcano,
      selectedProject,
      messages,
      addMessage,
      updateMessage,
      setMessages,
      setNewMessage,
      clearFiles,
      setIsSendingMessage,
      setCurrentThinkingStatus,
      setError,
      setLastTokenUsage,
      setChatElapsedSeconds,
      setStreamingMessageId,
      generateInlineVisualExplanationForMessage,
      logout,
    ]
  )

  /**
   * Stops ongoing message generation
   */
  const handleStopProcess = useCallback(() => {
    if (requestAbortControllerRef.current) {
      requestAbortControllerRef.current.abort()
      requestAbortControllerRef.current = null

      // Remove optimistic messages
      setMessages(messages.filter(msg => !msg.message_id.startsWith('temp-user-')))
    }

    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current)
      typingIntervalRef.current = null

      const streamingId = useChatStore.getState().streamingMessageId
      if (streamingId) {
        updateMessage(streamingId, msg => ({
          content: msg.content.trim() + '\n\n*[Generazione interrotta]*',
        }))
      }
    }

    setIsSendingMessage(false)
    setStreamingMessageId(null)
    setCurrentThinkingStatus('')

    if (chatTimerIntervalRef.current) {
      clearInterval(chatTimerIntervalRef.current)
      chatTimerIntervalRef.current = null
    }
    chatStartTimeRef.current = null
  }, [
    messages,
    setMessages,
    updateMessage,
    setIsSendingMessage,
    setStreamingMessageId,
    setCurrentThinkingStatus,
  ])

  /**
   * Handles magic prompt enhancement
   */
  const handleMagicPrompt = useCallback(async () => {
    if (
      !newMessage.trim() ||
      useChatStore.getState().isSendingMessage ||
      useUIStore.getState().isMagicLoading
    ) {
      return
    }

    setIsMagicLoading(true)
    setError('')

    try {
      const enhancedPrompt = await getMagicPromptChat(
        token,
        newMessage,
        magicPromptLength
      )
      setNewMessage(enhancedPrompt)
    } catch (err) {
      const errorMessage = extractErrorMessage(
        err,
        'Failed to generate magic prompt. Please try again.'
      )
      setError(errorMessage)
    } finally {
      setIsMagicLoading(false)
    }
  }, [newMessage, token, magicPromptLength, setNewMessage, setError, setIsMagicLoading])

  /**
   * Copies message content to clipboard
   */
  const handleCopyMessage = useCallback(
    (content, messageId) => {
      if (!content || typeof content !== 'string') return

      const plainText = removeMarkdown(content)
      navigator.clipboard
        .writeText(plainText)
        .then(() => {
          setCopiedMessageId(messageId)
          setTimeout(() => setCopiedMessageId(null), 2000)
        })
        .catch(err => {
          console.error('Failed to copy text: ', err)
          setError('Impossibile copiare il messaggio negli appunti.')
        })
    },
    [setCopiedMessageId, setError]
  )

  /**
   * Regenerates assistant response
   */
  const handleRegenerate = useCallback(
    async message => {
      if (!selectedConversation) return

      setRegeneratingMessageId(message.message_id)
      setError('')
      setCurrentThinkingStatus('Sto rigenerando la risposta...')
      setChatElapsedSeconds(0)

      chatStartTimeRef.current = Date.now()
      chatTimerIntervalRef.current = setInterval(() => {
        if (chatStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - chatStartTimeRef.current) / 1000)
          setChatElapsedSeconds(elapsed)
        }
      }, 1000)

      try {
        const response = await regenerateResponse(token, {
          conversationId: selectedConversation.id,
          arcanaId: ARCANA_ENABLED ? selectedArcano?.arcana_id : undefined,
          enableDeepAnalysis,
          enableKnowledgeBase,
        })

        const updatedMessages = messages.filter(
          m => m.role !== 'assistant' || m.message_id !== message.message_id
        )

        setMessages([
          ...updatedMessages,
          response.user_message,
          response.assistant_message,
        ])

        setCurrentThinkingStatus('')

        if (chatTimerIntervalRef.current) {
          clearInterval(chatTimerIntervalRef.current)
          chatTimerIntervalRef.current = null
        }
        chatStartTimeRef.current = null
      } catch (err) {
        setError('Errore durante la rigenerazione della risposta.')
      } finally {
        setRegeneratingMessageId(null)

        if (chatTimerIntervalRef.current) {
          clearInterval(chatTimerIntervalRef.current)
          chatTimerIntervalRef.current = null
        }
        chatStartTimeRef.current = null
      }
    },
    [
      selectedConversation,
      messages,
      token,
      selectedArcano,
      enableDeepAnalysis,
      enableKnowledgeBase,
      setMessages,
      setRegeneratingMessageId,
      setError,
      setCurrentThinkingStatus,
      setChatElapsedSeconds,
    ]
  )

  /**
   * Starts a new conversation
   */
  const handleNewConversation = useCallback(() => {
    if (requestAbortControllerRef.current) {
      requestAbortControllerRef.current.abort()
    }
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current)
    }

    resetChatState()
    clearFiles()
    useUIStore.getState().triggerInputFocus()
  }, [resetChatState, clearFiles])

  /**
   * Deletes a conversation
   */
  const handleDeleteConversation = useCallback(async () => {
    const conversationToDelete = useUIStore.getState().conversationToDelete
    if (!conversationToDelete || !token) return

    setError('')

    try {
      await axios.delete(
        `${API_BASE_URL}/chat/conversations/${conversationToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      useChatStore.getState().removeConversation(conversationToDelete.id)

      if (selectedConversation?.id === conversationToDelete.id) {
        handleNewConversation()
      }

      useUIStore.getState().closeDeleteConfirm()
    } catch (err) {
      console.error('Error deleting conversation:', err)
      setError('Impossibile eliminare la conversazione.')

      if (err.response?.status === 401 || err.response?.status === 403) {
        logout()
      }
    }
  }, [token, selectedConversation, handleNewConversation, setError, logout])

  /**
   * Deletes all conversations
   */
  const handleDeleteAllConversations = useCallback(async () => {
    if (!token) return

    setError('')

    try {
      await axios.delete(`${API_BASE_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      useChatStore.getState().clearAllConversations()
      handleNewConversation()
      useUIStore.getState().closeDeleteAllConfirm()
    } catch (err) {
      console.error('Error deleting all conversations:', err)
      setError('Impossibile eliminare tutte le conversazioni.')

      if (err.response?.status === 401 || err.response?.status === 403) {
        logout()
      }
    }
  }, [token, handleNewConversation, setError, logout])

  /**
   * Generates and attaches an inline visual explanation to an assistant message
   */
  const handleGenerateVisualExplanation = useCallback(
    async message => {
      if (!message?.message_id || message?.role !== 'assistant') return
      await generateInlineVisualExplanationForMessage(
        message.message_id,
        message.content,
        {
          force: true,
          conversationId: useChatStore.getState().selectedConversation?.id,
        }
      )
    },
    [generateInlineVisualExplanationForMessage]
  )

  /**
   * Exports chat to CSV
   */
  const exportChatToCSV = useCallback(
    (messages, selectedConversation) => {
      try {
        if (!messages || messages.length === 0) {
          setError('Nessun messaggio da esportare.')
          return
        }

        const rows = [['timestamp', 'role', 'content']]

        messages.forEach(m => {
          const ts = m.timestamp || ''
          const role = m.role || ''
          const content = typeof m.content === 'string' ? removeMarkdown(m.content) : ''
          rows.push([ts, role, content])
        })

        const escapeCsv = val => {
          if (val == null) return ''
          const str = String(val)
          const escaped = str.replace(/"/g, '""')
          if (/[",\n\r]/.test(escaped)) return `"${escaped}"`
          return escaped
        }

        const csvContent = rows.map(r => r.map(escapeCsv).join(',')).join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)

        const now = new Date()
        const pad = n => String(n).padStart(2, '0')
        const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
          now.getDate()
        )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
        const baseName = selectedConversation?.title || 'chat'
        const safeBase = baseName.replace(/[^a-zA-Z0-9-_ ]/g, '') || 'chat'
        const filename = `${safeBase}_${dateStr}.csv`

        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)

        try {
          a.click()
        } catch (e) {
          console.error('Export click failed', e)
        }

        try {
          if (document.body.contains(a)) document.body.removeChild(a)
        } catch (e) {
          console.warn('Could not remove temporary download link', e)
        }

        try {
          URL.revokeObjectURL(url)
        } catch (e) {}
      } catch (err) {
        console.error('Export CSV error', err)
        setError("Errore durante l'esportazione della chat.")
      }
    },
    [setError]
  )

  return {
    handleSendMessage,
    handleStopProcess,
    handleMagicPrompt,
    handleCopyMessage,
    handleRegenerate,
    handleNewConversation,
    handleDeleteConversation,
    handleDeleteAllConversations,
    handleGenerateVisualExplanation,
    exportChatToCSV,
  }
}
